<template>
  <div class="quill-editor-wrapper">
    <label v-if="label" class="editor-label">
      {{ label }}
    </label>
    <quill-editor
      ref="quillEditor"
      v-model="editorContent"
      :options="editorOptions"
      :class="editorClass"
      @blur="onBlur"
      @focus="onFocus"
      @change="onChange"
      @ready="onEditorReady"
    />
    <small v-if="errorMessage" class="text-danger d-block mt-1">
      {{ errorMessage }}
    </small>
    <small v-if="helperText" class="text-muted d-block mt-1">
      {{ helperText }}
    </small>
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

export default {
  name: 'QuillEditor',
  components: {
    quillEditor,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Nhập nội dung...',
    },
    minHeight: {
      type: String,
      default: '200px',
    },
    maxHeight: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    helperText: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      editorContent: this.value,
      quillInstance: null,
    };
  },
  computed: {
    editorOptions() {
      return {
        placeholder: this.placeholder,
        theme: 'snow',
        readOnly: this.disabled,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            [{ size: ['small', false, 'large', 'huge'] }],
            ['clean'],
          ],
          clipboard: {
            matchVisual: true,
          },
        },
        formats: [
          'bold',
          'italic',
          'underline',
          'strike',
          'header',
          'list',
          'bullet',
          'indent',
          'align',
          'size',
        ],
      };
    },
    editorClass() {
      return [
        'custom-quill-editor',
        this.customClass,
        { 'is-disabled': this.disabled },
        { 'has-error': this.errorMessage },
      ];
    },
  },
  watch: {
    value(newVal) {
      if (newVal !== this.editorContent) {
        this.editorContent = newVal;
      }
    },
    editorContent(newVal) {
      this.$emit('input', newVal);
    },
  },
  methods: {
    onEditorReady(quill) {
      this.quillInstance = quill;
      const editor = quill.root;
      editor.addEventListener('paste', (e) => {
        this.$emit('paste', {
          event: e,
          html: e.clipboardData.getData('text/html'),
          text: e.clipboardData.getData('text/plain'),
        });
      });
    },

    onBlur(quill) {
      this.$emit('blur', quill);
    },

    onFocus(quill) {
      this.$emit('focus', quill);
    },

    onChange({ quill, html, text }) {
      this.$emit('change', { quill, html, text });
    },

    getQuill() {
      return this.quillInstance || this.$refs.quillEditor?.quill;
    },

    getText() {
      return this.getQuill()?.getText() || '';
    },

    getLength() {
      return this.getQuill()?.getLength() || 0;
    },

    clear() {
      const quill = this.getQuill();
      if (quill) {
        quill.setText('');
      }
      this.editorContent = '';
    },

    setContent(content, source = 'api') {
      const quill = this.getQuill();
      if (!quill) return;

      if (typeof content === 'string') {
        const delta = quill.clipboard.convert(content);
        quill.setContents(delta, source);
      } else {
        quill.setContents(content, source);
      }

      this.editorContent = quill.root.innerHTML;
    },

    getDelta() {
      return this.getQuill()?.getContents();
    },
  },
};
</script>

<style scoped>
.quill-editor-wrapper {
  margin-bottom: 1rem;
}

.editor-label {
  display: block;
  margin-bottom: 0.5rem;
}

.custom-quill-editor >>> .ql-toolbar {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px 4px 0 0;
}

.custom-quill-editor >>> .ql-container {
  border: 1px solid #ced4da;
  border-radius: 0 0 4px 4px;
  background-color: white;
  font-size: 14px;
}

.custom-quill-editor >>> .ql-editor {
  min-height: v-bind(minHeight);
  max-height: v-bind(maxHeight);
  overflow-y: auto;
}

.custom-quill-editor.is-disabled >>> .ql-toolbar {
  background-color: #e9ecef;
  pointer-events: none;
  opacity: 0.6;
}

.custom-quill-editor.is-disabled >>> .ql-container {
  background-color: #e9ecef;
}

.custom-quill-editor.has-error >>> .ql-toolbar,
.custom-quill-editor.has-error >>> .ql-container {
  border-color: #dc3545;
}

.custom-quill-editor >>> .ql-editor ol,
.custom-quill-editor >>> .ql-editor ul {
  padding-left: 1.5em;
}

.custom-quill-editor >>> .ql-editor li {
  margin-bottom: 0.3em;
}

.custom-quill-editor >>> .ql-editor .ql-indent-1 {
  padding-left: 3em;
}

.custom-quill-editor >>> .ql-editor .ql-indent-2 {
  padding-left: 6em;
}

.custom-quill-editor >>> .ql-editor .ql-indent-3 {
  padding-left: 9em;
}

.custom-quill-editor >>> .ql-editor .ql-indent-4 {
  padding-left: 12em;
}

.custom-quill-editor >>> .ql-editor .ql-align-center {
  text-align: center;
}

.custom-quill-editor >>> .ql-editor .ql-align-right {
  text-align: right;
}

.custom-quill-editor >>> .ql-editor .ql-align-justify {
  text-align: justify;
}

.custom-quill-editor >>> .ql-editor .ql-size-small {
  font-size: 0.75em;
}

.custom-quill-editor >>> .ql-editor .ql-size-large {
  font-size: 1.5em;
}

.custom-quill-editor >>> .ql-editor .ql-size-huge {
  font-size: 2.5em;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
}

.text-muted {
  color: #6c757d;
  font-size: 0.875rem;
}

.d-block {
  display: block;
}

.mt-1 {
  margin-top: 0.25rem;
}
</style>
